import { useEffect, useState, useRef } from 'react';
import { Form, Input, Select, Button, Modal, TimePicker } from 'antd';
import { DeleteOutlined, SaveOutlined, EditOutlined } from '@ant-design/icons';
import moment from 'moment';
import EnergySelector from './EnergySelector';
import './WellnessForm.css';

const { TextArea } = Input;
const { Option } = Select;

const DAYS_OF_WEEK = [
   { value: 'Monday', label: 'Monday' },
   { value: 'Tuesday', label: 'Tuesday' },
   { value: 'Wednesday', label: 'Wednesday' },
   { value: 'Thursday', label: 'Thursday' },
   { value: 'Friday', label: 'Friday' },
   { value: 'Saturday', label: 'Saturday' },
   { value: 'Sunday', label: 'Sunday' },
];

const WellnessForm = ({
   onSave,
   onDelete,
   entry,
   selectedDate,
   loading,
   formDraft,
   onDraftChange,
}) => {
   const [form] = Form.useForm();
   const [isEditing, setIsEditing] = useState(false);
   const [energyValue, setEnergyValue] = useState(null);
   const isNewEntry = !entry?.id;
   const prevDateRef = useRef(null);
   const prevEntryIdRef = useRef(null);

   useEffect(() => {
      if (selectedDate) {
         const dateChanged = prevDateRef.current !== selectedDate;
         const entryChanged = prevEntryIdRef.current !== entry?.id;
         const entryDeleted = prevEntryIdRef.current && !entry?.id;

         prevDateRef.current = selectedDate;
         prevEntryIdRef.current = entry?.id;

         if (entry && entryChanged) {
            // Load entry data when entry changes
            const dayOfWeek = moment(entry.date).format('dddd');
            form.setFieldsValue({
               ...entry,
               day: dayOfWeek,
               sleepHours: entry.sleepHours ? moment(entry.sleepHours, 'HH:mm') : null,
            });
            setEnergyValue(entry.energy || null);
            setIsEditing(false);
         } else if (!entry && (dateChanged || entryDeleted)) {
            // New entry - reset form when date changes OR entry was deleted
            const dayOfWeek = moment(selectedDate).format('dddd');
            form.setFieldsValue({
               date: selectedDate,
               day: dayOfWeek,
               dailyIntentions: '',
               energy: null,
               restfulMoment: '',
               improvements: '',
               sleepHours: null,
               journal: '',
               breakfast: '',
               lunch: '',
               dinner: '',
               snacks: '',
               lookingForward: '',
            });
            setEnergyValue(null);
            setIsEditing(true);
         }
      }
   }, [selectedDate, entry, formDraft, form]);

   const saveDraft = (energy) => {
      const values = form.getFieldsValue();
      const draftData = {
         ...values,
         date: selectedDate,
         energy: energy,
         sleepHours: values.sleepHours ? moment(values.sleepHours).format('HH:mm') : null,
      };
      onDraftChange(draftData);
   };

   const handleFormChange = () => {
      saveDraft(energyValue);
   };

   const handleEnergyChange = value => {
      setEnergyValue(value);
      saveDraft(value);
   };

   const handleSave = async () => {
      try {
         const values = await form.validateFields();
         const sleepHoursFormatted = values.sleepHours ? moment(values.sleepHours).format('HH:mm') : null;

         if (entry?.id) {
            const updateData = {
               dailyIntentions: values.dailyIntentions,
               energy: energyValue,
               breakfast: values.breakfast,
               lunch: values.lunch,
               dinner: values.dinner,
               snacks: values.snacks,
               restfulMoment: values.restfulMoment,
               improvements: values.improvements,
               sleepHours: sleepHoursFormatted,
               journal: values.journal,
               lookingForward: values.lookingForward,
            };
            await onSave(entry.id, updateData, true);
         } else {
            const createData = {
               ...values,
               date: selectedDate,
               energy: energyValue,
               sleepHours: sleepHoursFormatted,
            };
            await onSave(null, createData, false);
         }
         setIsEditing(false);
      } catch (error) {
         console.error('Validation failed:', error);
      }
   };

   const handleDelete = () => {
      Modal.confirm({
         title: 'Delete Entry',
         content: 'Are you sure you want to delete this wellness entry?',
         okText: 'Delete',
         okType: 'danger',
         cancelText: 'Cancel',
         onOk: async () => {
            await onDelete(entry.id);
         },
      });
   };

   const formatDate = date => {
      return moment(date).format('DD/MM/YYYY');
   };

   return (
      <div className="wellness-form-container">
         <div className="form-header">
            <div className="form-header-left">
               <h3 className="form-title">
                  {isNewEntry ? 'New Entry' : 'Wellness Entry'}
               </h3>
               <span className="form-date">{moment(selectedDate).format('dddd, MMMM D, YYYY')}</span>
               <p className="wellness-update-hint">You can always update your entry.</p>
            </div>
            <div className="form-header-actions">
               {!isNewEntry && !isEditing && (
                  <Button
                     type="primary"
                     icon={<EditOutlined />}
                     onClick={() => setIsEditing(true)}
                  >
                     Edit
                  </Button>
               )}
            </div>
         </div>

         <div className="form-layout">
            {/* Left Column */}
            <div className="form-left">
               <Form
                  form={form}
                  layout="vertical"
                  className="wellness-form"
                  onValuesChange={handleFormChange}
                  disabled={!isEditing}
               >
                  {/* Day & Date */}
                  <div className="form-section">
                     <div className="form-row">
                        <Form.Item name="day" label="Day" className="form-item-half">
                           <Select disabled className="form-select">
                              {DAYS_OF_WEEK.map(day => (
                                 <Option key={day.value} value={day.value}>
                                    {day.label}
                                 </Option>
                              ))}
                           </Select>
                        </Form.Item>
                        <Form.Item label="Date" className="form-item-half">
                           <Input value={formatDate(selectedDate)} disabled className="form-input" />
                        </Form.Item>
                     </div>
                  </div>

                  {/* Daily Intentions */}
                  <div className="form-section">
                     <div className="section-label">
                        <span className="section-dot" style={{ background: '#f8bbd9' }} />
                        <span>Daily Intentions:</span>
                     </div>
                     <Form.Item name="dailyIntentions">
                        <TextArea
                           rows={4}
                           maxLength={1000}
                           showCount
                           placeholder="What are your intentions for today?"
                           className="form-textarea"
                        />
                     </Form.Item>
                     <span className="char-limit">Free format text space (1000 characters)</span>
                  </div>
               </Form>

               {/* Energy Level - Outside Form to avoid disabled context */}
               <EnergySelector
                  value={energyValue}
                  onChange={handleEnergyChange}
                  disabled={!isEditing}
               />

               <Form
                  form={form}
                  layout="vertical"
                  className="wellness-form"
                  onValuesChange={handleFormChange}
                  disabled={!isEditing}
               >
                  {/* Restful Moment */}
                  <div className="form-section">
                     <div className="section-label">
                        <span className="section-dot" style={{ background: '#1890ff' }} />
                        <span>Restful Moment - today</span>
                     </div>
                     <p className="section-hint">Example: "Reading my book with a coffee"</p>
                     <Form.Item name="restfulMoment">
                        <TextArea
                           rows={3}
                           maxLength={150}
                           showCount
                           placeholder=""
                           className="form-textarea"
                        />
                     </Form.Item>
                     <span className="char-limit">Free format text space (150 characters)</span>
                  </div>

                  {/* Improvements */}
                  <div className="form-section">
                     <div className="section-label">
                        <span className="section-dot" style={{ background: '#52c41a' }} />
                        <span>What could be improved</span>
                     </div>
                     <Form.Item name="improvements">
                        <TextArea
                           rows={3}
                           maxLength={250}
                           showCount
                           placeholder=""
                           className="form-textarea"
                        />
                     </Form.Item>
                     <span className="char-limit">Free format text space (250 characters)</span>
                  </div>
               </Form>
            </div>

            {/* Right Column */}
            <div className="form-right">
               <Form
                  form={form}
                  layout="vertical"
                  className="wellness-form"
                  onValuesChange={handleFormChange}
                  disabled={!isEditing}
               >
                  {/* Sleep */}
                  <div className="form-section">
                     <div className="section-label inline-label">
                        <span className="section-dot" style={{ background: '#1890ff' }} />
                        <span>Sleep - total hours last night</span>
                        <Form.Item name="sleepHours" className="inline-input">
                           <TimePicker
                              format="HH:mm"
                              placeholder="hh:mm"
                              className="form-timepicker"
                           />
                        </Form.Item>
                     </div>
                  </div>

                  {/* What did you eat today - moved to right column */}
                  <div className="form-section food-section">
                     <div className="section-label">
                        <span className="section-dot" style={{ background: '#fa8c16' }} />
                        <span>What did you eat today</span>
                     </div>
                     <Form.Item name="breakfast" label="Breakfast:">
                        <Input placeholder="" className="form-input" />
                     </Form.Item>
                     <Form.Item name="lunch" label="Lunch:">
                        <Input placeholder="" className="form-input" />
                     </Form.Item>
                     <Form.Item name="dinner" label="Dinner:">
                        <Input placeholder="" className="form-input" />
                     </Form.Item>
                     <Form.Item name="snacks" label="Any yummy snacks:">
                        <Input placeholder="" className="form-input" />
                     </Form.Item>
                  </div>

                  {/* Journal */}
                  <div className="form-section journal-section">
                     <div className="section-label">
                        <span className="section-dot" style={{ background: '#fadb14' }} />
                        <span>Journal - Yours thoughts and feelings:</span>
                     </div>
                     <Form.Item name="journal">
                        <TextArea
                           rows={10}
                           maxLength={2000}
                           showCount
                           placeholder=""
                           className="form-textarea"
                        />
                     </Form.Item>
                     <span className="char-limit">Free format text space (2000 characters)</span>
                  </div>

                  {/* Looking Forward */}
                  <div className="form-section">
                     <div className="section-label">
                        <span className="section-dot" style={{ background: '#9254de' }} />
                        <span>Looking Forward</span>
                     </div>
                     <Form.Item name="lookingForward">
                        <TextArea
                           rows={3}
                           maxLength={500}
                           showCount
                           placeholder=""
                           className="form-textarea"
                        />
                     </Form.Item>
                     <span className="char-limit">Free format text space (500 characters)</span>
                  </div>
               </Form>
            </div>
         </div>

         {/* Action Buttons */}
         <div className="form-actions">
            {!isNewEntry && (
               <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                  loading={loading}
                  size="large"
               >
                  Delete Entry
               </Button>
            )}
            <div className="action-spacer" />
            {isEditing && (
               <>
                  {!isNewEntry && (
                     <Button size="large" onClick={() => setIsEditing(false)}>
                        Cancel
                     </Button>
                  )}
                  <Button
                     type="primary"
                     icon={<SaveOutlined />}
                     onClick={handleSave}
                     loading={loading}
                     size="large"
                  >
                     Save Entry
                  </Button>
               </>
            )}
         </div>
      </div>
   );
};

export default WellnessForm;
